/**
 * FlashDirectWidget — W5 Flash Info, accès direct.
 *
 * Identique aux ClassicWidgets (W6-W9) : card cliquable → /flashinfo/feed.
 * ArrowUpRight badge top-right. Corps = texte simple (pas de boutons).
 *
 * DECISIONS #167 · Phase V3.1
 */

import { useNavigate } from 'react-router-dom';
import { useAnimate, motion } from 'framer-motion';
import { Zap, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { peauWidget, DecorWidgetActif } from './peauWidget';
import { ROUTES } from '@/lib/routes';
import { useT } from '@/lib/i18n';

interface FlashDirectWidgetProps {
  isActive?: boolean;
}

export function FlashDirectWidget({ isActive = false }: FlashDirectWidgetProps) {
  const navigate = useNavigate();
  const [scope, animate] = useAnimate();
  const t = useT(); // `#798` i18n — nom + sous-titre du catalogue

  function handleNavigate() {
    try {
      void animate('.fw-ripple',
        { scale: [0, 4.5], opacity: [0.4, 0] },
        { duration: 0.52, ease: [0.22, 1, 0.36, 1] },
      );
      void animate('.fw-shimmer', { x: [-90, 280] }, { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] });
      void animate('.fw-arrow',   { x: [0, 6], opacity: [1, 0.4] }, { duration: 0.22, ease: 'easeOut' });
    } catch { /* navigation ne doit jamais être bloquée */ }
    navigate(ROUTES.flashinfoFeed);
  }

  return (
    <motion.button
      ref={scope}
      type="button"
      data-testid="flash-direct-widget"
      onClick={handleNavigate}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 600, damping: 35 }}
      className={cn(peauWidget(isActive))}
      aria-pressed={isActive}
    >
      {/* Active decorations */}
      {isActive && (
        <DecorWidgetActif />
      )}

      {/* ── Ripple de lancement ──────────────────────────────────────────── */}
      <span
        className="fw-ripple pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-samsung-400/0 opacity-0"
        aria-hidden
      />

      {/* ── Shimmer clip ─────────────────────────────────────────────────── */}
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl" aria-hidden>
        <span
          className="fw-shimmer absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-transparent via-brand-samsung-400/18 to-transparent"
          style={{ transform: 'translateX(-96px)' }}
        />
      </span>

      {/* Header : icône + label + ArrowUpRight */}
      <div className="flex items-center justify-between mb-1.5 shrink-0 relative">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={cn(
            'shrink-0 flex h-[20px] w-[20px] items-center justify-center rounded-md [&_svg]:h-3.5 [&_svg]:w-3.5 transition-all duration-200',
            isActive
              ? 'bg-brand-samsung-500/35 text-brand-samsung-700 dark:text-brand-samsung-200 shadow-[0_0_8px_rgba(20,40,160,0.45)]'
              : 'bg-white/[0.05] text-muted-foreground/40',
          )}>
            <Zap />
          </span>
          <span className={cn(
            'text-[9px] font-semibold uppercase tracking-widest truncate transition-colors duration-200',
            isActive ? 'text-brand-samsung-700 dark:text-brand-samsung-300/90' : 'text-muted-foreground/70',
          )}>
            {t('nav.flashInfo')}
          </span>
        </div>

        <span className={cn(
          'fw-arrow shrink-0 flex h-[20px] w-[20px] items-center justify-center rounded-md border transition-all duration-200',
          isActive
            ? 'border-brand-samsung-500 bg-brand-samsung-500 text-white shadow-[0_0_8px_rgba(20,40,160,0.4)]'
            : 'border-[var(--border-default,var(--surface-border))] text-muted-foreground/30 group-hover:border-brand-samsung-500 group-hover:bg-brand-samsung-500 group-hover:text-white',
        )}>
          <ArrowUpRight className="h-3 w-3" />
        </span>
      </div>

      {/* Corps — texte simple */}
      <div className="flex-1 flex items-center relative">
        <span className={cn(
          'text-[13px] font-bold leading-tight line-clamp-2 transition-colors duration-200',
          isActive ? 'text-brand-samsung-700 dark:text-brand-samsung-100' : 'text-foreground',
        )}>
          {t('nav.flashInfo.sous')}
        </span>
      </div>
    </motion.button>
  );
}
